import { Intercom, IntercomUserUpdateOptions } from '@capacitor-community/intercom';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { isPlatform } from '@ionic/react';
import { useIntercom } from 'react-use-intercom';
import { getUserHash } from 'src/api/intercom';
import { useCallback, useMemo } from 'react';

export default function useIntercomPlugin() {
  const { update, boot, show, hide, shutdown, showMessages, trackEvent, showArticle } = useIntercom();

  const displayArticle = useCallback(
    async (articleId: number) => {
      if (isPlatform('hybrid')) {
        Haptics.impact({ style: ImpactStyle.Light });
        await Intercom.displayArticle({ articleId: articleId.toString() });
      } else {
        showArticle(articleId);
      }
    },
    [showArticle]
  );

  const showIntercomMessages = useCallback(async () => {
    if (isPlatform('hybrid')) {
      await Intercom.displayMessageComposer({ message: '' });
    } else {
      showMessages();
    }
  }, [showMessages]);

  const updateUser = useCallback(
    async (params: IntercomUserUpdateOptions) => {
      const userHash = params.userId ? await getUserHash(params.userId) : undefined;
      if (isPlatform('hybrid')) {
        Intercom.updateUser({
          ...params,
          customAttributes: { ...params.customAttributes, user_type: 'client' },
        });
      } else {
        update({
          ...params,
          userHash,
          customAttributes: { ...params.customAttributes, user_type: 'client' },
        });
      }
    },
    [update]
  );

  const registerUser = useCallback(async (options: { userId?: string; email?: string }) => {
    if (!isPlatform('hybrid')) return;
    const userHash = options.userId ? await getUserHash(options.userId) : undefined;

    userHash && (await Intercom.setUserHash({ hmac: userHash }));

    if (options?.userId || options?.email) await Intercom.registerIdentifiedUser(options);
    else await Intercom.registerUnidentifiedUser();
  }, []);

  const bootPlugin = useCallback(
    async (params: IntercomUserUpdateOptions) => {
      if (isPlatform('hybrid')) {
        // TODO(maylorsan): delete logout here
        await Intercom.logout();
        await Intercom.hideLauncher();
        await registerUser({ userId: params?.userId });
      } else {
        const userHash = params.userId ? await getUserHash(params.userId) : undefined;
        boot({
          ...params,
          hideDefaultLauncher: true,
          customAttributes: { ...params.customAttributes, user_type: 'client' },
          ...(userHash && { userHash }),
        });
      }
    },
    [registerUser, boot]
  );

  const clearUser = useCallback(
    async (lang: string) => {
      if (isPlatform('hybrid')) await Intercom.logout();
      else await shutdown();
      await bootPlugin({ languageOverride: lang, customAttributes: {} });
    },
    [shutdown, bootPlugin]
  );

  const setBottomPadding = useCallback(
    (paddingInPxForWeb: number) => {
      const iosPadding = paddingInPxForWeb ? paddingInPxForWeb - 30 : paddingInPxForWeb;
      const androidPadding = paddingInPxForWeb ? paddingInPxForWeb + 100 : paddingInPxForWeb;

      const resultPadding = isPlatform('ios') ? iosPadding : isPlatform('android') ? androidPadding : paddingInPxForWeb;

      isPlatform('hybrid')
        ? Intercom.setBottomPadding({ value: resultPadding.toString() })
        : update({ verticalPadding: resultPadding });
    },
    [update]
  );

  const hideLauncher = useCallback(() => {
    isPlatform('hybrid') ? Intercom.hideLauncher() : update({ hideDefaultLauncher: true });
  }, [update]);

  const showLauncher = useCallback(() => {
    isPlatform('hybrid') ? Intercom.displayLauncher() : update({ hideDefaultLauncher: false });
  }, [update]);

  const showMessenger = useCallback(async () => {
    return isPlatform('hybrid') ? Intercom.displayMessenger() : show();
  }, [show]);

  const hideMessenger = useCallback(async () => {
    return isPlatform('hybrid') ? Intercom.hideMessenger() : hide();
  }, [hide]);

  const logEvent = useCallback(
    async (name: string, data?: object) => {
      return isPlatform('hybrid') ? Intercom.logEvent({ name, data }) : trackEvent(name, data);
    },
    [trackEvent]
  );

  return useMemo(
    () => ({
      logEvent,
      updateUser,
      bootPlugin,
      setBottomPadding,
      showLauncher,
      hideLauncher,
      registerUser,
      showMessenger,
      hideMessenger,
      clearUser,
      showIntercomMessages,
      displayArticle,
    }),
    [
      logEvent,
      updateUser,
      bootPlugin,
      setBottomPadding,
      showLauncher,
      hideLauncher,
      registerUser,
      showMessenger,
      hideMessenger,
      clearUser,
      showIntercomMessages,
      displayArticle,
    ]
  );
}
